import { apiClient } from "./client";
import type { Hotel, RatePlan, RoomType } from "@/types/hotel";

// ── AI Extract types ──────────────────────────────────────────────

export interface AiExtractAddress {
  address_line1: string | null;
  city: string | null;
  postal_code: string | null;
  province: string | null;
  country_code: string | null;
  country_name: string | null;
  latitude: number | null;
  longitude: number | null;
  parking_type: string | null;
  parking_info: string | null;
}

export interface AiExtractFacility {
  category: string;
  key: string;
  enabled: boolean;
  value: Record<string, any> | null;
}

export interface AiExtractPolicy {
  check_in_time: string | null;
  check_out_time: string | null;
  cancellation_policy: string | null;
  accepted_payments: string[] | null;
  smoking_allowed: boolean | null;
  minimum_age: number | null;
}

export interface AiExtractContact {
  phone_reception: string | null;
  email_general: string | null;
  email_reservations: string | null;
  social_media: Record<string, string> | null;
  review_scores: Record<string, number> | null;
}

export interface AiExtractRoomType {
  name: string;
  code: string;
  bed_type: string | null;
  description: string | null;
  max_adults: number | null;
  max_children: number | null;
  max_occupants: number | null;
  total_inventory: number | null;
  area_sqm: number | null;
  amenities: string[];
}

export interface AiExtractRatePlan {
  name: string;
  code: string;
  is_refundable: boolean | null;
  includes_breakfast: boolean | null;
  includes_lunch: boolean | null;
  includes_dinner: boolean | null;
  meal_plan: string | null;
  cancellation_policy: string | null;
  min_stay: number | null;
  max_stay: number | null;
  description: string | null;
}

export interface AiExtractResponse {
  hotel: Record<string, any>;
  address: AiExtractAddress | null;
  facilities: AiExtractFacility[];
  policies: AiExtractPolicy | null;
  contacts: AiExtractContact | null;
  room_types: AiExtractRoomType[];
  rate_plans: AiExtractRatePlan[];
  fields_filled: number;
  fields_total: number;
  sections_saved: string[];
}

export async function createMyHotel(name: string): Promise<Hotel> {
  const res = await apiClient.post<Hotel>("/hotels/create-mine", { name });
  return res.data;
}

export async function deleteHotel(hotelId: string): Promise<void> {
  await apiClient.delete(`/hotels/${hotelId}`);
}

export async function getHotels(): Promise<Hotel[]> {
  const res = await apiClient.get<Hotel[]>("/hotels");
  return res.data;
}

export async function getHotel(hotelId: string): Promise<Hotel> {
  const res = await apiClient.get<Hotel>(`/hotels/${hotelId}`);
  return res.data;
}

// ── Room Types ────────────────────────────────────────────────────

export async function getRoomTypes(hotelId: string): Promise<RoomType[]> {
  const res = await apiClient.get<RoomType[]>(`/hotels/${hotelId}/room-types`);
  return res.data;
}

export async function createRoomType(
  hotelId: string,
  data: {
    name: string;
    code: string;
    total_inventory: number;
    max_adults?: number;
    max_children?: number;
    max_occupants?: number;
    description?: string | null;
    bed_type?: string | null;
    attributes?: Record<string, any> | null;
    sort_order?: number;
  },
): Promise<RoomType> {
  const res = await apiClient.post<RoomType>(`/hotels/${hotelId}/room-types`, data);
  return res.data;
}

export async function updateRoomType(
  hotelId: string,
  roomTypeId: string,
  data: {
    name?: string;
    total_inventory?: number;
    max_adults?: number;
    max_children?: number;
    max_occupants?: number;
    attributes?: Record<string, any> | null;
    sort_order?: number;
    is_active?: boolean;
  },
): Promise<RoomType> {
  const res = await apiClient.put<RoomType>(`/hotels/${hotelId}/room-types/${roomTypeId}`, data);
  return res.data;
}

// ── Rate Plans ────────────────────────────────────────────────────

export async function getRatePlans(hotelId: string): Promise<RatePlan[]> {
  const res = await apiClient.get<RatePlan[]>(`/hotels/${hotelId}/rate-plans`);
  return res.data;
}

export async function createRatePlan(
  hotelId: string,
  data: {
    name: string;
    code: string;
    cancellation_policy?: string | null;
    is_refundable?: boolean;
    includes_breakfast?: boolean;
    includes_lunch?: boolean | null;
    includes_dinner?: boolean | null;
    meal_plan?: string | null;
    min_stay?: number | null;
    max_stay?: number | null;
    description?: string | null;
    visible_web?: boolean;
    visible_aria?: boolean;
    visible_walkin?: boolean;
  },
): Promise<RatePlan> {
  const res = await apiClient.post<RatePlan>(`/hotels/${hotelId}/rate-plans`, data);
  return res.data;
}

export async function getRatePlansForRoomType(
  hotelId: string,
  roomTypeId: string,
): Promise<RatePlan[]> {
  const res = await apiClient.get<RatePlan[]>(
    `/hotels/${hotelId}/room-types/${roomTypeId}/rate-plans`,
  );
  return res.data;
}

export async function updateRatePlan(
  hotelId: string,
  ratePlanId: string,
  data: {
    name?: string;
    cancellation_policy?: string | null;
    is_refundable?: boolean;
    includes_breakfast?: boolean;
    attributes?: Record<string, any> | null;
    is_active?: boolean;
    visible_web?: boolean;
    visible_aria?: boolean;
    visible_walkin?: boolean;
  },
): Promise<RatePlan> {
  const res = await apiClient.put<RatePlan>(`/hotels/${hotelId}/rate-plans/${ratePlanId}`, data);
  return res.data;
}

// ── Hotel Update ──────────────────────────────────────────────────

export async function updateHotel(
  hotelId: string,
  data: {
    name?: string;
    currency?: string;
    timezone?: string;
    locale?: string;
    city?: string | null;
    region?: string | null;
    country?: string | null;
    phone?: string | null;
    email?: string | null;
    star_rating?: number | null;
    total_rooms?: number | null;
    description?: string | null;
    website?: string | null;
  },
): Promise<Hotel> {
  const res = await apiClient.put<Hotel>(`/hotels/${hotelId}`, data);
  return res.data;
}

// ── AI Data Extraction ────────────────────────────────────────────

export async function aiExtractHotelData(
  hotelId: string,
  formData: FormData,
): Promise<AiExtractResponse> {
  const res = await apiClient.post<AiExtractResponse>(
    `/hotels/${hotelId}/ai-extract`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return res.data;
}

export async function aiExtractHotelDataDeep(
  hotelId: string,
  formData: FormData,
): Promise<AiExtractResponse> {
  const res = await apiClient.post<AiExtractResponse>(
    `/hotels/${hotelId}/ai-extract-deep`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 300_000 },
  );
  return res.data;
}
